import { useTranslation } from 'react-i18next'
import { CreditCard, ScrollText, Users } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSettingsModal } from '../settings/settings-modal/SettingsModalProvider'
import { useBillingVisible } from '../../features/billing/useBillingVisible'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'
import BrandMark from '../layout/BrandMark'
import UserMenu, { UserMenuItem } from '../layout/UserMenu'
import UserMenuTeamSection from '../teams/UserMenuTeamSection'
import UserMenuActiveTeamLabel from '../teams/UserMenuActiveTeamLabel'
import { HEADER_ACTIONS_GAP, HEADER_PADDING_X, HEADER_ROW } from '../layout/headerGeometry'
import { TEMPLATES_GALLERY_ENABLED } from '../../constants/featureFlags'
import { createNadDebug } from '../../utils/debugLog'
import { trackInteraction } from '../../analytics/interactionAnalytics'
import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { canViewPostpaidUsage, isPostpaidTeam } from '../../features/billing/teamBillingMode'

const accountSettingsLog = createNadDebug('dw:account-settings-modal')

// ⚠️ Keep in sync: the pre-consent cookie backdrop
// (apps/console/src/components/consent/ConsentBackdrop.tsx) is a static visual
// copy of this navbar (logged-out state). If you change the nav layout/branding,
// update ConsentBackdrop too. It must stay import-free of app code (see CLAUDE.md
// → UI/UX Guidelines → "Cookie consent backdrop").
//
// Brand + profile menu are the shared BrandMark / UserMenu, and the shell geometry
// (horizontal padding, height, max-width, item gap) is the shared headerGeometry.ts
// SSOT — the single source of truth with the app dashboard header (layout/Header).
// ConsentBackdrop hand-mirrors these same values (it must stay import-free), and a
// vitest guard asserts its inlined values still equal the SSOT. This navbar's
// rendered output is intentionally unchanged (the constants equal its geometry
// verbatim).
export default function NavbarV2() {
  const { t } = useTranslation('landing')
  const navigate = useNavigate()
  const { user, isAuthenticated, signOut } = useAuth()
  // Shared dialog owned above <Routes> — the landing is NOT inside <Layout>,
  // so this surface could not be served by a layout-scoped provider.
  const { open: openSettingsModal } = useSettingsModal()
  const billingVisible = useBillingVisible()
  const teamsEnabled = useTeamsEnabled()
  const { activeTeam, isLoading: teamsLoading } = useActiveTeam()
  const billingModeReady = !teamsEnabled || (!teamsLoading && activeTeam !== null)
  const postpaid = isPostpaidTeam(activeTeam)
  const billingEntryVisible = billingModeReady && (billingVisible || canViewPostpaidUsage(activeTeam))
  // Same predicate the settings-section registry uses, so the menu entry and
  // the section it opens cannot disagree about whether Teams exists.
  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }


  return (
    <>
      {/* `absolute`, never `fixed`: the nav scrolls away with the hero instead
          of docking to the top. It previously swapped to a fixed solid-black bar
          past 400px, which is why the pill could not simply be unconditional. */}
      <nav
        data-testid="landing-navbar"
        className="absolute left-0 right-0 top-[var(--banner-height,0px)] z-50 bg-transparent"
      >
        <div className={HEADER_PADDING_X}>
          {/* One floating pill for BOTH auth states. ConsentBackdrop
              hand-mirrors this row (RFC-0782 / RQ10773473) and cannot import it,
              so the value lives in headerGeometry with a CI guard. */}
          <div
            data-testid="landing-navbar-shell"
            data-variant="pill"
            className={HEADER_ROW}
          >
          {/* Brand */}
          <div>
            <BrandMark
              surface="landing"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          </div>

          {/* Right side */}
          <div className={`flex items-center ${HEADER_ACTIONS_GAP}`}>
            {/* ⚠️ Mirror this link in ConsentBackdrop.tsx (static <span>) — see the
                sync note at the top of this file. Dark-launched behind
                TEMPLATES_GALLERY_ENABLED (RFC-0934 P1). */}
            {TEMPLATES_GALLERY_ENABLED && (
              <Link
                to="/templates"
                data-testid="nav-templates-link"
                className="hidden text-sm font-medium text-white/80 no-underline transition-colors hover:text-white sm:inline"
              >
                {t('navbarV2.templates')}
              </Link>
            )}
            {isAuthenticated && user ? (
              <>
                {/* Plain nav link inside the pill rather than a solid white
                    button — in the mockup the signed-in nav is a row of quiet
                    text links with the avatar as the only emphasised element.
                    Only routes that exist are wired: the mockup's Explore /
                    Pricing / About entries have no routes in this app and were
                    deliberately not invented. */}
                <Link
                  to="/dashboard"
                  data-testid="nav-projects-link"
                  className="rounded-[40px] px-3 py-2 text-sm font-medium text-white/80 no-underline transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:px-4"
                >
                  {t('navbarV2.projects')}
                </Link>

                <UserMenu
                  user={user}
                  testIdPrefix="homenav"
                  accountTitle={t('navbarV2.account')}
                  avatarAlt={t('navbarV2.avatar')}
                  userFallbackLabel={t('navbarV2.userFallback')}
                  leading={teamsEnabled ? <UserMenuActiveTeamLabel /> : null}
                >
                  {(close) => (
                    <>
                      {/* Same team row + flyout as the app header. The landing
                          is where a signed-in user arrives first, so "which
                          team am I spending from" has to be answerable here
                          too, not only after navigating to /dashboard. */}
                      <UserMenuTeamSection closeMenu={close} />
                      <UserMenuItem
                        testId="homenav-profile-button"
                        onClick={() => {
                          close()
                          openSettingsModal('profile')
                        }}
                        icon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        }
                      >
                        {t('navbarV2.profile')}
                      </UserMenuItem>
                      <UserMenuItem
                        testId="homenav-settings-button"
                        onClick={() => {
                          close()
                          accountSettingsLog({
                            event: 'menu-settings-click',
                            trace_id: 'account-settings-modal:landing',
                            surface: 'landing',
                            entry_point: 'landing-user-menu',
                            section: 'general',
                          })
                          openSettingsModal('general')
                        }}
                        icon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        }
                      >
                        {t('navbarV2.settings')}
                      </UserMenuItem>
                      <UserMenuItem
                        testId="homenav-rules-button"
                        onClick={() => {
                          close()
                          openSettingsModal('personalization')
                        }}
                        icon={<ScrollText className="w-4 h-4" />}
                      >
                        {t('navbarV2.myRules')}
                      </UserMenuItem>
                      {/* Mirrors the dashboard header's Teams entry. The team
                          row above answers "which team am I in"; this is the
                          way to manage one, and omitting it here made Teams
                          reachable only after navigating to /dashboard. */}
                      {teamsEnabled && (
                        <UserMenuItem
                          testId="homenav-teams-button"
                          onClick={() => {
                            close()
                            openSettingsModal('teams')
                          }}
                          icon={<Users className="w-4 h-4" strokeWidth={2} />}
                        >
                          {t('navbarV2.teams')}
                        </UserMenuItem>
                      )}
                      {billingEntryVisible && (
                        <UserMenuItem
                          testId="homenav-usage-credits-button"
                          onClick={() => {
                            close()
                            openSettingsModal('usage-credits')
                          }}
                          icon={<CreditCard className="w-4 h-4" strokeWidth={2} />}
                        >
                          {postpaid ? t('navbarV2.usage') : t('navbarV2.usageAndCredits')}
                        </UserMenuItem>
                      )}
                      <UserMenuItem
                        onClick={() => {
                          close()
                          handleSignOut()
                        }}
                        icon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        }
                      >
                        {t('navbarV2.signOut')}
                      </UserMenuItem>
                    </>
                  )}
                </UserMenu>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => trackInteraction('auth_signin_clicked', { entry_point: 'navbar' })}
                data-testid="sign-in-btn"
                className="rounded-[40px] bg-white px-4 py-2.5 text-sm font-semibold text-black no-underline transition-colors hover:bg-white/90 md:px-6 md:py-3"
              >
                {t('navbarV2.signIn')}
              </Link>
            )}
          </div>
        </div>
        </div>
      </nav>

    </>
  )
}
